// Activation ticks for stale-while-revalidate on return (keep-alive-page-host
// AC3/AC4). The page host bumps a per-key counter each time a *kept* page is
// re-activated — but never on its first mount, which already fetches. A page's
// data hook subscribes to its key's tick and fires a *silent* background
// re-fetch on each bump; the last-known content holds the spot until the fresh
// data lands (no blank-then-populate).
//
// The bump rule is tied directly to keep-alive: a page revalidates on activation
// iff it was *already mounted* when it became active — which is exactly the
// "visited before and kept" condition. A first visit is not yet mounted, so it
// does not bump (its initial mount fetches). A remount-on-return page is never
// mounted when inactive, so it never bumps — it re-fetches from empty via its
// fresh mount, which is the AC5 contract.
//
// Kept out of PageHost so the rule is unit-testable without React.

export type ActivationTicks = Record<string, number>;

// Given the previous tick map, whether the arriving key was already mounted
// (i.e. a kept page being returned to), and the key now becoming active, return
// the next tick map. Only an already-mounted key bumps — a return — and only it.
export function nextActivationTicks(
  prev: ActivationTicks,
  alreadyMounted: boolean,
  active: string,
): ActivationTicks {
  if (!alreadyMounted) return prev; // first visit (or a remount page) → no bump
  return { ...prev, [active]: (prev[active] ?? 0) + 1 };
}
